import * as React from 'react';
import axios from 'axios';
import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Container, Alert, CircularProgress, Box, Typography, Paper, Grid, Button, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import Chip from '@mui/material/Chip';

const DEFAULT_IMAGE = 'https://lp.ondara.group/assets/img/property-default.jpg';

function getImageUrl(backendApi, dealId, imageId) {
  if (!imageId) return DEFAULT_IMAGE;
  return `${backendApi}getimage.php?module=Deals&id=${dealId}&attachment_id=${imageId}`;
}

function OportunidadDetalle(props) {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [detalle, setDetalle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [downloadingDocId, setDownloadingDocId] = useState(null);
  const [optingLoading, setOptingLoading] = useState(false);

  useEffect(() => {
    axios
      .post(props.config.backend_api + "getopportunitydetail.php", { id: id })
      .then((response) => {
        if (response.data.status === "success") {
          setDetalle(response.data.data.detalle);
        }
        if (response.data.status === "error") {
          setError(response.data.message);
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setError('Error al cargar los detalles de la oportunidad.');
        setLoading(false);
      });
  }, [id, props.config.backend_api]);

  const handleBack = () => {
    const from = location.state?.from || '/dashboard/opportunities';
    navigate(from);
  };

  const handlePrevImage = () => {
    if (detalle && detalle.Images.length > 1) {
      setCurrentImageIndex((prev) => (prev === 0 ? detalle.Images.length - 1 : prev - 1));
    }
  };

  const handleNextImage = () => {
    if (detalle && detalle.Images.length > 1) {
      setCurrentImageIndex((prev) => (prev === detalle.Images.length - 1 ? 0 : prev + 1));
    }
  };

  const handleOptar = () => {
    setOptingLoading(true);
    const userData = JSON.parse(sessionStorage.getItem("userData"));
    axios
      .post(props.config.backend_api + "optaropportunity.php", {
        id_oferta: id,
        userId: userData.userId
      })
      .then((response) => {
        if (response.data.status === "success") {
          setModalOpen(true);
        } else {
          setError(response.data.message);
        }
      })
      .catch((err) => {
        console.error(err);
        setError('Error al procesar la solicitud.');
      })
      .finally(() => {
        setOptingLoading(false);
      });
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleDownloadDocument = async (doc) => {
    setDownloadingDocId(doc.id);
    try {
      const response = await axios.post(
        props.config.backend_api + 'downloadattachment.php',
        {
          module: 'Deals',
          id: detalle.id,
          attachment_id: doc.id
        },
        { responseType: 'blob' }
      );

      if (response.data && typeof response.data === 'object' && response.data.type === 'application/json') {
        const reader = new FileReader();
        reader.onload = () => {
          try {
            const errorData = JSON.parse(reader.result);
            setError(errorData.message || 'Error al descargar el documento');
          } catch {
            setError('Error al descargar el documento');
          }
        };
        reader.readAsText(response.data);
        return;
      }

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.File_Name || `documento_${doc.id}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      setError('Error al descargar el documento: ' + (doc.File_Name || doc.id));
    } finally {
      setDownloadingDocId(null);
    }
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error || !detalle) {
    return (
      <Container sx={{ py: 2 }}>
        <Alert severity="error">{error || 'Oportunidad no encontrada.'}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ mt: 2 }}>
          Volver
        </Button>
      </Container>
    );
  }

  const formatCurrency = (value) => {
    if (!value) return 'N/A';
    return Number(value).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' });
  };

  const direccion = [detalle.Street, detalle.City, detalle.Zip_Code, detalle.State].filter(Boolean).join(', ');

  return (
    <Container maxWidth="xl" sx={{ py: 3, mt: 8}}>
      <Button startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ mb: 2 }}>
        Ver todas las Oportunidades
      </Button>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={4} sx={{ mb: 2 , p: 4 }}>
        <Grid xs={12} lg={8} sx={{ mb: 2 , p: 4 }}>
          <Paper sx={{ p: 3, backgroundColor: '#eee' }} elevation={2} >
            <Box sx={{ borderBottom: 1, borderColor: 'divider', pb: 2, mb: 3 }}>
              <Typography variant="h5" component="h1" sx={{ fontWeight: 600, mb: 2 }}>
                {detalle.Deal_Name || 'Sin nombre'}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {direccion || 'Dirección no disponible'}
              </Typography>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Box sx={{ position: 'relative', mb: 2 }}>
                <Box
                  sx={{
                    width: '100%',
                    height: 400,
                    backgroundImage: `url(${getImageUrl(props.config.backend_api, detalle.id, detalle.Images[currentImageIndex])})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    borderRadius: 2,
                  }}
                />
                {detalle.Images.length > 1 && (
                  <>
                    <Button
                      onClick={handlePrevImage}
                      sx={{
                        position: 'absolute',
                        left: 8,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        minWidth: 40,
                        height: 40,
                        bgcolor: 'rgba(255,255,255,0.8)',
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.95)' },
                      }}
                    >
                      ‹
                    </Button>
                    <Button
                      onClick={handleNextImage}
                      sx={{
                        position: 'absolute',
                        right: 8,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        minWidth: 40,
                        height: 40,
                        bgcolor: 'rgba(255,255,255,0.8)',
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.95)' },
                      }}
                    >
                      ›
                    </Button>
                  </>
                )}
              </Box>
              {detalle.Images.length > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                  {detalle.Images.map((img, index) => (
                    <Box
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      sx={{
                        width: 60,
                        height: 60,
                        backgroundImage: `url(${getImageUrl(props.config.backend_api, detalle.id, img)})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        borderRadius: 1,
                        cursor: 'pointer',
                        border: index === currentImageIndex ? '2px solid #000' : '2px solid transparent',
                        opacity: index === currentImageIndex ? 1 : 0.6,
                      }}
                    />
                  ))}
                </Box>
              )}
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, borderBottom: 2, borderColor: '#000', display: 'inline-block' }}>
                Detalles
              </Typography>
              <Grid container spacing={0} sx={{ mt: 1 }}>
                <Grid xs={12} md={6}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box sx={{ display: 'flex' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, width: 180 }}>REFERENCIA</Typography>
                      <Typography variant="body2">{detalle.Expediente || 'N/A'}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, width: 180 }}>VALOR DE MERCADO</Typography>
                      <Typography variant="body2">{formatCurrency(detalle.Valor_venta_real_y_actual)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, width: 180 }}>PRECIO ORIENTATIVO</Typography>
                      <Typography variant="body2">{formatCurrency(detalle.Amount)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, width: 180 }}>VIVIENDA</Typography>
                      <Typography variant="body2">{detalle.Tipo_de_activo || 'N/A'}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, width: 180 }}>DIRECCIÓN</Typography>
                      <Typography variant="body2">{direccion || 'N/A'}</Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid xs={12} md={6}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box sx={{ display: 'flex' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, width: 180 }}>Comunidad autónoma</Typography>
                      <Typography variant="body2">{detalle.Comunidad_autonoma || 'N/A'}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, width: 180 }}>Provincia</Typography>
                      <Typography variant="body2">{detalle.Provincia || 'N/A'}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, width: 180 }}>Municipio</Typography>
                      <Typography variant="body2">{detalle.Ciudad || 'N/A'}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, width: 180 }}>Código postal</Typography>
                      <Typography variant="body2">{detalle.Codigo_postal || 'N/A'}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, width: 180 }}>Metros construidos</Typography>
                      <Typography variant="body2">{detalle.Metros_construidos || 'N/A'}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, width: 180 }}>Metros suelo</Typography>
                      <Typography variant="body2">{detalle.Metros_suelo || 'N/A'}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, width: 180 }}>Vpo</Typography>
                      <Typography variant="body2">{detalle.Vpo || 'N/A'}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, width: 180 }}>Año construcción</Typography>
                      <Typography variant="body2">{detalle.Ano_construccion || 'N/A'}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, width: 180 }}>Referencia catastral</Typography>
                      <Typography variant="body2">{detalle.Ref_Catastral || 'N/A'}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, width: 180 }}>Estado ocupacional</Typography>
                      <Typography variant="body2">{detalle.Situacion_finca || 'N/A'}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, width: 180 }}>Titularidad sobre inmueble</Typography>
                      <Typography variant="body2">{detalle.Titularidad_sobre_inmueble || 'N/A'}</Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, borderBottom: 2, borderColor: '#000', display: 'inline-block' }}>
                Documentos
              </Typography>
              <Box sx={{ mt: 2 }}>
                {detalle.Documents && detalle.Documents.length > 0 ? (
                  detalle.Documents.map((doc) => (
                    <Button
                      key={doc.id}
                      onClick={() => handleDownloadDocument(doc)}
                      startIcon={<PictureAsPdfIcon style={{ color: 'red', marginRight: 8 }} fontSize="small" />}
                      sx={{ mb: 1, justifyContent: 'flex-start', width: '100%' }}
                      disabled={downloadingDocId === doc.id}
                    >
                      {downloadingDocId === doc.id ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        doc.File_Name
                      )}
                    </Button>
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No hay documentos disponibles para esta oportunidad.
                  </Typography>
                )}
              </Box>
            </Box>
          </Paper>
        </Grid>

        <Grid xs={12} lg={4} sx={{ mb: 2 , p: 4 }}>
          <Paper sx={{ p: 3, position: 'sticky', top: 20, backgroundColor: '#eee' }} elevation={2}>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Chip label={detalle.Tipo_de_activo || 'Inmueble'} sx={{ mb: 2 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {detalle.Deal_Name || 'Sin nombre'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Ref: {detalle.Expediente || 'N/A'}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center', py: 4, bgcolor: '#f5f5f5', borderRadius: 2, mb: 3 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 2 }}>
                ¿Quieres participar en esta operación?
              </Typography>
              <Button
                variant="contained"
                size="large"
                fullWidth
                onClick={handleOptar}
                disabled={optingLoading}
                sx={{
                  bgcolor: '#000',
                  color: '#fff',
                  '&:hover': { bgcolor: '#333' },
                }}
              >
                {optingLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Optar a esta oportunidad'
                )}
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Dialog open={modalOpen} onClose={handleCloseModal}>
        <DialogTitle sx={{ textAlign: 'center', pt: 4 }}>
          Hemos recibido tu interés en esta oportunidad
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', pb: 2 }}>
          <Typography variant="body1" color="text.secondary">
            En breve nos pondremos en contacto contigo
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button onClick={handleCloseModal} variant="contained" sx={{ bgcolor: '#000', '&:hover': { bgcolor: '#333' } }}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default OportunidadDetalle;